import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Switch,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  AdminPanelSettings as AdminIcon,
  Replay as ReplayIcon,
  School as TutorialIcon,
  Speed as SpeedIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import MainLayout from '../components/MainLayout';
import { apiService } from '../services/apiService';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { useAppSelector } from '../hooks/redux';
import { useTutorial } from '../hooks/useTutorial';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../config/firebase';

const Settings: React.FC = () => {
  const { isAdmin } = useRoleAccess();
  const { mongoUser, user } = useAppSelector((state) => state.auth);
  const { resetTutorial, isLoading: tutorialLoading, isTutorialCompleted } = useTutorial();

  // Profile state
  const [userName, setUserName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);

  // Speech Rate Settings state
  const [speechRateSettings, setSpeechRateSettings] = useState({
    enabled: true,
    time_window: 30,
    max_words: 150,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (mongoUser?.name) {
      setUserName(mongoUser.name);
    }
  }, [mongoUser]);

  useEffect(() => {
    if (isAdmin) {
      fetchSpeechRateSettings();
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  const handleUpdateProfile = async () => {
    try {
      setSavingProfile(true);
      setError(null);
      setSuccess(null);

      const response = await apiService.put('/auth/profile', {
        name: userName,
      });

      if (response.success) {
        setSuccess('Profile updated successfully');
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!user?.email) {
      setError('No email address found');
      return;
    }

    try {
      setSendingPasswordReset(true);
      setError(null);
      setSuccess(null);

      await sendPasswordResetEmail(auth, user.email);
      setSuccess(`Password reset email sent to ${user.email}`);
      setTimeout(() => setSuccess(null), 8000);
    } catch (err: any) {
      console.error('Error sending password reset:', err);
      setError(err.message || 'Failed to send password reset email');
    } finally {
      setSendingPasswordReset(false);
    }
  };

  const fetchSpeechRateSettings = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/speech-rate');
      if (response.success && response.data) {
        setSpeechRateSettings({
          enabled: response.data.enabled,
          time_window: response.data.time_window,
          max_words: response.data.max_words,
        });
      }
    } catch (err: any) {
      console.error('Error fetching speech rate settings:', err);
      setError('Failed to load speech rate settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSpeechRate = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await apiService.put('/speech-rate', speechRateSettings);
      if (response.success) {
        setSuccess('Speech rate settings updated successfully');
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err: any) {
      console.error('Error saving speech rate settings:', err);
      setError(err.message || 'Failed to save speech rate settings');
    } finally {
      setSaving(false);
    }
  };

  const getRoleDisplayName = (role: string) => {
    return role === 'admin' ? 'Administrator' : 'Salesperson';
  };

  return (
    <MainLayout title="Settings">
      <div className="p-2">
        {/* Page Header */}
        <div className="mb-3">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your profile and system preferences</p>
      </div>

      {/* Alerts */}
      {error && (
        <Alert severity="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" className="mb-4" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Profile Section */}
        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            p: 3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          <Box className="flex items-center gap-2 mb-4">
            <PersonIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6">User Profile</Typography>
          </Box>

          <div className="space-y-4" style={{ maxWidth: 350 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              variant="outlined"
              size="small"
            />

            <TextField
              fullWidth
              label="Email Address"
              value={mongoUser?.email || user?.email || ''}
              disabled
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.disabled', fontSize: 20 }} />,
              }}
              helperText="Email cannot be changed"
            />

            <Box>
              <Typography variant="body2" color="textSecondary" className="mb-1">
                Account Role
              </Typography>
              <Chip
                icon={<AdminIcon />}
                label={getRoleDisplayName(mongoUser?.role || 'user')}
                color={isAdmin ? 'primary' : 'default'}
                variant={isAdmin ? 'filled' : 'outlined'}
                size="small"
              />
            </Box>
          </div>

          <Box className="flex justify-end pt-4">
            <Button
              variant="contained"
              onClick={handleUpdateProfile}
              disabled={savingProfile || !userName || userName === mongoUser?.name}
              startIcon={<SaveIcon />}
              size="small"
            >
              {savingProfile ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>

        {/* Security Section */}
        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            p: 3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          <Box className="flex items-center gap-2 mb-4">
            <LockIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6">Security</Typography>
          </Box>

          <Box
            sx={{
              backgroundColor: 'grey.50',
              borderRadius: 1,
              p: 2,
            }}
          >
            <Typography variant="body2" fontWeight={500} className="mb-1">
              Password Reset
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Send a password reset link to your email address to change your password.
            </Typography>
            <Box className="flex justify-end mt-4">
              <Button
                variant="outlined"
                startIcon={<LockIcon />}
                onClick={handlePasswordReset}
                disabled={sendingPasswordReset}
                size="small"
              >
                {sendingPasswordReset ? 'Sending...' : 'Send Reset Email'}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Speech Rate Settings - Admin Only */}
        {isAdmin && (
          <Box
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 2,
              p: 3,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}
            data-tutorial="speech-rate-section"
          >
            <Box className="flex items-center gap-2 mb-4">
              <SpeedIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6">Speech Rate Monitoring</Typography>
            </Box>

            {loading ? (
              <Box className="flex justify-center py-6">
                <CircularProgress />
              </Box>
            ) : (
              <>
                <div className="space-y-4" style={{ maxWidth: 350 }}>
                  <Box className="flex items-center justify-between">
                    <Typography variant="body2" color="textSecondary">
                      Enable Monitoring
                    </Typography>
                    <Switch
                      checked={speechRateSettings.enabled}
                      onChange={(e) =>
                        setSpeechRateSettings({
                          ...speechRateSettings,
                          enabled: e.target.checked,
                        })
                      }
                      color="primary"
                    />
                  </Box>

                  <TextField
                    fullWidth
                    label="Time Window (seconds)"
                    type="number"
                    size="small"
                    value={speechRateSettings.time_window}
                    onChange={(e) =>
                      setSpeechRateSettings({
                        ...speechRateSettings,
                        time_window: parseInt(e.target.value),
                      })
                    }
                    helperText="Analysis period: 1-120 seconds"
                    disabled={!speechRateSettings.enabled}
                    inputProps={{ min: 1, max: 120 }}
                  />

                  <TextField
                    fullWidth
                    label="Max Words per Window"
                    type="number"
                    size="small"
                    value={speechRateSettings.max_words}
                    onChange={(e) =>
                      setSpeechRateSettings({
                        ...speechRateSettings,
                        max_words: parseInt(e.target.value),
                      })
                    }
                    helperText="Warning threshold: 1-500 words"
                    disabled={!speechRateSettings.enabled}
                    inputProps={{ min: 1, max: 500 }}
                  />
                </div>

                <Box className="flex justify-end pt-4">
                  <Button
                    variant="contained"
                    onClick={handleSaveSpeechRate}
                    disabled={saving}
                    startIcon={<SaveIcon />}
                    size="small"
                  >
                    {saving ? 'Saving...' : 'Save Speech Rate Settings'}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}

        {/* Tutorial Section */}
        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            p: 3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
          data-tutorial="tutorial-section"
        >
          <Box className="flex items-center gap-2 mb-4">
            <TutorialIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6">Guided Tutorial</Typography>
          </Box>

          <Typography variant="body2" color="textSecondary">
            {isTutorialCompleted
              ? 'You have completed the tutorial. You can restart it anytime.'
              : 'Start the guided tour to learn about the key features.'}
          </Typography>
          <Box className="flex justify-end mt-4">
            <Button
              variant="outlined"
              startIcon={tutorialLoading ? <CircularProgress size={16} /> : <ReplayIcon />}
              onClick={resetTutorial}
              disabled={tutorialLoading}
              size="small"
            >
              {tutorialLoading ? 'Starting...' : isTutorialCompleted ? 'Restart Tutorial' : 'Start Tutorial'}
            </Button>
          </Box>
        </Box>
        </div>
      </div>
    </MainLayout>
  );
};

export default Settings;
